// 매장 운영설정 + 현재 영업/예약 상태 훅.
// HomePage/CartPage 의 영업안내 위젯과, 3-C 의 주문 화면 예약 슬롯 셀렉터에서 공유한다.

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  computeOpenStatus,
  listReservationSlots,
  type OpenStatus,
  type ReservationDay,
  type StoreSettingsDto,
} from "@oo/shared";
import { getStoreSettings } from "./operationsApi.ts";

// 매장 로컬 타임존. MVP 단일 매장 가정으로 코드 상수로 둔다.
// 향후 다매장/다국가 확장 시 store_settings 컬럼으로 이관.
const STORE_TIMEZONE = "Asia/Seoul";

// 현재 시각을 매장 로컬 시각(year/month/day/hour/minute) 으로 재해석한 Date 를 만든다.
// 클라이언트 디바이스 타임존과 무관하게 매장 기준 시각을 다루기 위한 변환.
//  - Intl.DateTimeFormat 으로 KST 의 yyyy/mm/dd/hh/mm 을 추출
//  - 그 값으로 new Date(...) (로컬 컨스트럭터) 를 만든다 - JS Date.getHours 등이 그대로 반환된다.
function nowInStoreLocal(): Date {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: STORE_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date());
  const get = (type: string) => Number.parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
  return new Date(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
}

export interface UseStoreSettingsResult {
  settings: StoreSettingsDto | undefined;
  isLoading: boolean;
  isError: boolean;
  // 현재 시점 즉시 주문 가능 여부.
  openStatus: OpenStatus | null;
  // 예약 주문 슬롯 일자별 그루핑.
  reservationDays: ReservationDay[];
  // 매장 로컬 기준 현재 Date (컴포넌트에서 시간 비교에 사용).
  nowLocal: Date | null;
}

// 매장 운영설정 조회 훅.
export function useStoreSettings(): UseStoreSettingsResult {
  const query = useQuery({
    queryKey: ["store-settings", "public"],
    queryFn: getStoreSettings,
    // 운영설정은 자주 바뀌지 않는다. 한 화면에서 여러 컴포넌트가 사용해도 단일 fetch 면 충분.
    staleTime: 60_000,
  });
  return useMemo<UseStoreSettingsResult>(() => {
    if (!query.data) {
      return {
        settings: undefined,
        isLoading: query.isLoading,
        isError: query.isError,
        openStatus: null,
        reservationDays: [],
        nowLocal: null,
      };
    }
    const nowLocal = nowInStoreLocal();
    const openStatus = computeOpenStatus(query.data, nowLocal);
    const reservationDays = listReservationSlots(query.data, nowLocal);
    return {
      settings: query.data,
      isLoading: false,
      isError: false,
      openStatus,
      reservationDays,
      nowLocal,
    };
  }, [query.data, query.isLoading, query.isError]);
}
